import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ArrowLeft, LayoutTemplate, Code2 } from "lucide-react";

import { EtiquetaDesigner } from "@/components/EtiquetaDesigner";
import { EditorSimples } from "@/components/EtiquetaDesigner/EditorSimples";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { alert } from "@/contexts/AlertModalContext";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import inovaone_api from "@/services/inovaone_api";
import type { ListaZplItem } from "@/types/lista-zpl-item";

type Modo = "simples" | "avancada";

const CHAVE_MODO = "etiqueta_editor_modo";

const lerModoSalvo = (): Modo =>
  localStorage.getItem(CHAVE_MODO) === "avancada" ? "avancada" : "simples";

function EditorEtiqueta() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { impressaoConfig, getImpressaoConfig } = useImpressaoConfig();
  const [salvando, setSalvando] = useState(false);
  const [modo, setModo] = useState<Modo>(lerModoSalvo);

  const nomeEditado = params.get("nome");
  const lista = useMemo(
    () => impressaoConfig?.lista_zpl ?? [],
    [impressaoConfig?.lista_zpl],
  );

  const salvo = useMemo(
    () => (nomeEditado ? lista.find((z) => z.nome === nomeEditado) : null),
    [lista, nomeEditado],
  );

  // rascunho compartilhado: alternar de modo não perde o que já foi montado
  const [rascunho, setRascunho] = useState<ListaZplItem | null>(null);
  const rascunhoRef = useRef<ListaZplItem | null>(null);
  const registrarRascunho = useCallback((item: ListaZplItem) => {
    rascunhoRef.current = item;
  }, []);

  const trocarModo = (novo: Modo) => {
    if (!novo || novo === modo) return;
    setRascunho(rascunhoRef.current);
    setModo(novo);
    localStorage.setItem(CHAVE_MODO, novo);
  };

  const nomesExistentes = useMemo(() => lista.map((z) => z.nome), [lista]);

  const voltar = () => navigate("/etiquetas");

  const salvar = async (item: ListaZplItem) => {
    if (!impressaoConfig?._id) {
      alert.error("Configuração de impressão não encontrada.");
      return;
    }
    setSalvando(true);
    try {
      const novaLista = nomeEditado
        ? lista.map((z) => (z.nome === nomeEditado ? item : z))
        : [...lista, item];
      await inovaone_api.post(`/impressao_config`, [
        { _id: impressaoConfig._id, lista_zpl: novaLista },
      ]);
      await getImpressaoConfig();
      alert.success(nomeEditado ? "Etiqueta atualizada." : "Etiqueta criada.");
      voltar();
    } catch {
      alert.error("Erro ao salvar a etiqueta.");
    } finally {
      setSalvando(false);
    }
  };

  if (nomeEditado && impressaoConfig && !salvo) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        <Card>
          <CardContent className="py-16 text-center space-y-3">
            <p className="text-sm font-medium">
              Etiqueta “{nomeEditado}” não encontrada.
            </p>
            <Button size="sm" variant="outline" onClick={voltar}>
              <ArrowLeft className="h-4 w-4" />
              Voltar para etiquetas
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const valorInicial = rascunho ?? salvo ?? null;

  return (
    <div className="flex flex-col gap-3 py-4 md:py-6 px-4 lg:px-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="ghost" onClick={voltar}>
          <ArrowLeft className="h-4 w-4" />
          Etiquetas
        </Button>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold tracking-tight truncate">
            {nomeEditado ? `Editando: ${nomeEditado}` : "Nova etiqueta"}
          </h2>
          <p className="text-xs text-muted-foreground">
            {modo === "simples"
              ? "Modo simples: formulário e código ZPL."
              : "Modo avançado: monte a etiqueta arrastando os elementos."}
          </p>
        </div>

        <TooltipProvider delayDuration={300}>
          <ToggleGroup
            type="single"
            size="sm"
            value={modo}
            onValueChange={(v) => trocarModo(v as Modo)}
            className="ml-auto"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <ToggleGroupItem value="simples" className="h-8 gap-1.5 px-3">
                  <Code2 className="h-4 w-4" />
                  <span className="text-xs">Simples</span>
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent>
                Formulário + ZPL, como antes
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <ToggleGroupItem value="avancada" className="h-8 gap-1.5 px-3">
                  <LayoutTemplate className="h-4 w-4" />
                  <span className="text-xs">Avançada</span>
                </ToggleGroupItem>
              </TooltipTrigger>
              <TooltipContent>
                Editor visual com textos, imagens e códigos
              </TooltipContent>
            </Tooltip>
          </ToggleGroup>
        </TooltipProvider>
      </div>

      {modo === "simples" ? (
        <EditorSimples
          key={`simples-${nomeEditado ?? "nova"}`}
          valorInicial={valorInicial}
          nomesExistentes={nomesExistentes}
          salvando={salvando}
          onSalvar={salvar}
          onCancelar={voltar}
          onRascunho={registrarRascunho}
        />
      ) : (
        <EtiquetaDesigner
          key={`avancada-${nomeEditado ?? "nova"}`}
          valorInicial={valorInicial}
          nomesExistentes={nomesExistentes}
          salvando={salvando}
          onSalvar={salvar}
          onCancelar={voltar}
          onRascunho={registrarRascunho}
        />
      )}
    </div>
  );
}

export default EditorEtiqueta;
